import React from 'react';
import { getAllAchievements, ACHIEVEMENT_RARITY } from '../../../data/achievementDefinitions';

const AchievementDisplay = ({ achievements = [], stats = {} }) => {
  const allAchievements = getAllAchievements();
  const unlockedAchievementIds = achievements || [];

  const getAchievementProgress = (achievement) => {
    const { requirement } = achievement;
    const achievementStats = stats.stats || {};

    let current = 0;
    let target = requirement.value;

    switch (requirement.type) {
      case 'shifts_completed':
        current = achievementStats.totalShifts || 0;
        break;
      case 'inspections_completed':
        current = achievementStats.totalInspections || 0;
        break;
      case 'login_streak':
        current = achievementStats.longestStreak || 0;
        break;
      case 'night_shifts_completed':
        current = achievementStats.nightShiftsCompleted || 0;
        break;
      default:
        current = 0;
    }

    return {
      current: Math.min(current, target),
      target,
      percent: Math.min((current / target) * 100, 100)
    };
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case ACHIEVEMENT_RARITY.COMMON:
        return 'border-gray-300 bg-gray-50';
      case ACHIEVEMENT_RARITY.RARE:
        return 'border-blue-300 bg-blue-50';
      case ACHIEVEMENT_RARITY.EPIC:
        return 'border-purple-300 bg-purple-50';
      case ACHIEVEMENT_RARITY.LEGENDARY:
        return 'border-yellow-300 bg-yellow-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  const unlockedAchievements = allAchievements.filter(a => unlockedAchievementIds.includes(a.achievementId));
  const lockedAchievements = allAchievements.filter(a => !unlockedAchievementIds.includes(a.achievementId));

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">🏆 Achievements</h3>
          <div className="text-sm text-gray-600">
            ได้รับแล้ว: <span className="font-bold text-blue-600">{unlockedAchievements.length}</span> / {allAchievements.length}
          </div>
        </div>
      </div>

      {/* Unlocked Achievements */}
      {unlockedAchievements.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">✅ ได้รับแล้ว</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {unlockedAchievements.map((achievement) => {
              const rarityColor = getRarityColor(achievement.rarity);
              return (
                <div
                  key={achievement.achievementId}
                  className={`border-2 rounded-lg p-4 ${rarityColor}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-4xl">{achievement.icon}</div>
                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">ได้รับแล้ว</span>
                  </div>
                  <h5 className="font-semibold text-gray-800 mb-1">{achievement.nameThai}</h5>
                  <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                  <div className="text-xs text-gray-600">
                    <div>+{achievement.reward.points} Points</div>
                    <div>+{achievement.reward.xp} XP</div>
                    <div>+{achievement.reward.coins} Coins</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      {lockedAchievements.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">🔒 ยังไม่ได้รับ</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lockedAchievements.map((achievement) => {
              const progress = getAchievementProgress(achievement);
              const rarityColor = getRarityColor(achievement.rarity);
              return (
                <div
                  key={achievement.achievementId}
                  className={`border-2 rounded-lg p-4 border-gray-200 bg-gray-50 opacity-60`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-4xl opacity-50">{achievement.icon}</div>
                  </div>
                  <h5 className="font-semibold text-gray-800 mb-1">{achievement.nameThai}</h5>
                  <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>
                  
                  {/* Progress */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>ความคืบหน้า</span>
                      <span>{progress.current}/{progress.target}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all"
                        style={{ width: `${progress.percent}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Rewards Preview */}
                  <div className="text-xs text-gray-500">
                    <div>รางวัล: +{achievement.reward.points} Points</div>
                    <div>+{achievement.reward.xp} XP</div>
                    <div>+{achievement.reward.coins} Coins</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementDisplay;



